import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { JBAlert, JBButton, JBTypography } from '@jbhi-fi/lanyard-ui';
import CustomerHelper from '../../helpers/CustomerHelper';
import { useMobileVerifyContext } from '../../context/MobileVerifyContext';
import { MobileVerifyResponseType } from '../../hooks/useMobileVerify';
import { useTracking, useTrackingContext, trackingContext } from '@jbhi-fi/components/src/Tracking';

const customerHelper = new CustomerHelper();

const ALREADY_VERIFIED_WARNING_MESSAGE =
  'Please note that by linking this mobile number to your account, the existing account may lose some features';
const PERKS_FEATURE_WORDING = 'e.g. JB Perks.';

const AlreadyVerifiedView = ({
  handleRedirect,
}: {
  handleRedirect: (data: MobileVerifyResponseType) => void;
}): JSX.Element => {
  const history = useHistory();
  const { data, setMobileVerifyData } = useMobileVerifyContext();
  const { tracker } = useTracking({
    context: trackingContext.modal,
    group: 'mobile already taken',
    category: useTrackingContext().tracker.category,
  });
  const [submitting, setSubmitting] = useState<boolean>();
  const [sendError, setSendError] = useState<boolean>();
  const isPerksEnabled = !window.Jbhifi.isPerksDisabled;

  const warningMessage = `${ALREADY_VERIFIED_WARNING_MESSAGE}${isPerksEnabled ? ` ${PERKS_FEATURE_WORDING}` : '.'}`;

  useEffect(() => {
    // Update page title
    document.title = 'Mobile number already verified - Account - JB Hi-Fi';
    tracker.trackView();
  }, []);

  const getMobileNumber = (mobileNumber: string): string =>
    mobileNumber ? customerHelper.formatMobileNumber(mobileNumber) : '';

  const confirmPhoneNumber = async (): Promise<void> => {
    setSubmitting(true);
    tracker.trackClick({ label: 'yes' });

    try {
      const mobileNumber = getMobileNumber(data.mobileNumber);
      let response = await customerHelper.setCustomerMobile(data.customerId, mobileNumber, true);
      response = { ...response, mobileNumber, customerId: data.customerId };
      setMobileVerifyData(response);

      if (!response.fetchError) handleRedirect(response);
      else throw new Error(JSON.stringify(response));
    } catch (error) {
      tracker.trackError({ errorMsg: "Can't send verification code", contextOverride: trackingContext.form });
      setSendError(true);
      setSubmitting(false);
    }
  };

  const changePhoneNumber = (): void => {
    tracker.trackClick({ label: 'change number' });

    const verifyData = { ...data, isSuccess: true, isAmbiguous: false, customerId: undefined, mobileNumber: undefined };
    setMobileVerifyData(verifyData);
    window.location.href.includes('/account/login')
      ? history.push('/link-your-in-store-receipts')
      : history.push(`/manage-account/${data.currentMobileNumber ? 'change' : 'add'}-mobile-number`);
  };

  return (
    <>
      <div className="alerts">
        {sendError ? (
          <JBAlert
            showIcon
            type="error"
            heading="Can't send verification code"
            content="We were unable to send your receipt verification pin, please contact customer service."
          />
        ) : null}
      </div>
      <div className="page-width verify-section">
        <JBTypography type="h2" margin="20px 0px 24px">
          Mobile number already verified
        </JBTypography>
        <JBTypography type="p">
          This mobile number <strong>{customerHelper.formatMobileNumber(data.mobileNumber)}</strong> has already been
          linked to an existing account.
        </JBTypography>
        <JBTypography type="p">{warningMessage}</JBTypography>
        <JBTypography type="p">
          <strong>Is this your correct mobile number?</strong>
        </JBTypography>
        <div className="verify-section__actions">
          <JBButton
            type="primary"
            label={submitting ? 'Sending' : 'Yes'}
            onClick={submitting ? null : confirmPhoneNumber}
            submit
          />
          <JBButton
            type="link"
            classes="verify-section__actions-left"
            label="Change number"
            onClick={submitting ? null : changePhoneNumber}
          />
        </div>
      </div>
    </>
  );
};

export default AlreadyVerifiedView;
